/**
 * FortifySkillEffect — Earth tree wall fork 2 active (fortify).
 *
 * While stationary (≥1000ms), the player activates to grow stone armor —
 * a 30 HP absorption shield (separate HP pool) that intercepts all incoming
 * damage before it reaches the player's main HP.
 *
 * The shield persists until broken by damage or until the player moves
 * (with a 1-second grace period after movement starts). The cooldown begins
 * when the shield breaks or the movement-linger expires — NOT at activation.
 * This design rewards keeping the shield alive.
 *
 * Upgrade interactions resolved at activation and damage-absorption time:
 * - Bulwark (fortify_upgrade_1): +20 shield HP (30 → 50 total); absorbs 1
 *   stagger attempt per activation. Stagger absorption does not prevent HP
 *   damage from the triggering hit.
 * - Rebound (fortify_upgrade_2): when shield breaks from damage, detonates
 *   outward — 8 damage and 1-tile knockback in a 1.5-tile radius.
 *
 * External integration points:
 * - CombatHitResolver calls absorbDamage(incoming) before applying to main HP.
 * - StatusEffectSystem calls absorbStagger() before applying stagger.
 * - Game layer calls onMovementStart() when the player begins moving.
 *
 * Exposes playerState.fortifyShieldHp (> 0 when shield active) and
 * playerState.fortifyStaggerAbsorbsRemaining for consumption by other
 * SkillEffects (e.g., StandingWaveSkillEffect checks fortifyShieldHp > 0
 * for the Held Ground bonus).
 *
 * Cooldown tier: long (2000ms).
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Wall Fork 2 — Fortify Sub-Path
 */

import { InstantSkillEffect } from '../../InstantSkillEffect.js';

// ---------------------------------------------------------------------------
// Player state interface (mutable — FortifySkillEffect writes to these)
// ---------------------------------------------------------------------------

/**
 * Slice of player state owned by FortifySkillEffect.
 * Written on activation and on shield break/expiry.
 * Read by StandingWaveSkillEffect (Held Ground: fortifyShieldHp > 0 check)
 * and by CombatHitResolver/StatusEffectSystem (fortifyStaggerAbsorbsRemaining).
 */
export interface FortifyPlayerState {
  /** Current shield HP. 0 means shield is inactive. */
  fortifyShieldHp: number;
  /** Stagger absorbs remaining for this activation (0 without Bulwark, 1 with). */
  fortifyStaggerAbsorbsRemaining: number;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface FortifyDeps {
  readonly stationary: {
    /**
     * Returns true when the player has been continuously stationary for at
     * least `thresholdMs` ms. Shared StationaryTracker instance.
     */
    isStationary(thresholdMs: number): boolean;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    /** Returns undefined if no active upgrade carries the key. */
    getMaxEffect(skillId: string, key: string): number | undefined;
    hasEffect(skillId: string, key: string): boolean;
  };
  /** Mutable player state shared with other systems that read Fortify's shield status. */
  readonly playerState: FortifyPlayerState;
  readonly targeting: {
    /** Returns enemy IDs within the given pixel radius of the player. */
    getEnemiesInRadius(radiusPx: number): string[];
    /** Returns the player's current world position. */
    getPlayerPosition(): { x: number; y: number };
    /** Returns the world position of the named enemy. */
    getEnemyPosition(enemyId: string): { x: number; y: number };
  };
  readonly combat: {
    applyDamage(
      targetId: string,
      damage: number,
      opts: { sourceSkillId: string },
    ): void;
    applyKnockback(
      targetId: string,
      normalizedDir: { x: number; y: number },
      tiles: number,
    ): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface FortifyConfig {
  /** Long cooldown tier. Starts on shield break, not on activation. */
  readonly cooldownMs: number;
  /** Base absorption HP before Bulwark's +20 additive bonus. */
  readonly baseShieldHp: number;
  /** Stationary duration required before activation is allowed. */
  readonly stationaryRequirementMs: number;
  /** Grace period after movement starts before the shield deactivates. */
  readonly movementLingerMs: number;
  /** World units per tile — converts tile distances to pixels for Rebound radius. */
  readonly tileSizePx: number;
}

const DEFAULT_CONFIG: FortifyConfig = {
  cooldownMs: 2000,              // long cooldown tier
  baseShieldHp: 30,              // 30 base HP; Bulwark adds +20
  stationaryRequirementMs: 1000, // 1000ms stationary threshold (same as Standing Wave)
  movementLingerMs: 1000,        // 1s grace period after movement starts
  tileSizePx: 32,                // TILE_SIZE constant
};

// ---------------------------------------------------------------------------
// Damage absorption result
// ---------------------------------------------------------------------------

export interface DamageAbsorptionResult {
  /** HP absorbed by the shield (always ≥ 0). */
  readonly absorbed: number;
  /** Damage that overflows to the player's main HP pool (0 unless shield depleted). */
  readonly overflow: number;
  /** Whether this hit depleted the shield to 0. */
  readonly shieldDepleted: boolean;
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class FortifySkillEffect extends InstantSkillEffect {
  readonly skillId = 'fortify';

  private readonly deps: FortifyDeps;
  private readonly config: FortifyConfig;

  // Cooldown — starts on shield break or linger expiry, NOT on activation
  private _cooldownRemaining = 0;

  // Linger state — 1-second movement grace period
  private _isLingering = false;
  private _lingerRemaining = 0;

  // Per-activation telemetry (readable by simulation scenarios)
  private _activationCount = 0;
  /** How many times the cooldown has been started (should be 1 per activation cycle). */
  private _cooldownStartCount = 0;
  /** Number of enemies hit by the most recent Rebound explosion. */
  private _lastBreakExplosionTargetCount = 0;
  /** Result of the most recent absorbDamage() call. */
  private _lastAbsorptionResult: DamageAbsorptionResult | null = null;

  constructor(deps: FortifyDeps, config: Partial<FortifyConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  override get cooldown(): number { return this._cooldownRemaining; }

  /** Current shield HP. Mirrors playerState.fortifyShieldHp. */
  get shieldHp(): number { return this.deps.playerState.fortifyShieldHp; }

  /**
   * Whether the shield is functionally active — either shield HP remains
   * or the movement-linger timer is still counting down.
   */
  get isShieldActive(): boolean {
    return this.deps.playerState.fortifyShieldHp > 0 || this._isLingering;
  }

  /** Whether the movement-linger timer is currently running. */
  get isLingering(): boolean { return this._isLingering; }

  get activationCount(): number { return this._activationCount; }
  get cooldownStartCount(): number { return this._cooldownStartCount; }
  get lastBreakExplosionTargetCount(): number { return this._lastBreakExplosionTargetCount; }
  get lastAbsorptionResult(): DamageAbsorptionResult | null { return this._lastAbsorptionResult; }
  get staggerAbsorbsRemaining(): number { return this.deps.playerState.fortifyStaggerAbsorbsRemaining; }
  get stationaryRequirementMs(): number { return this.config.stationaryRequirementMs; }

  // --- InstantSkillEffect interface ---

  /**
   * Override: Fortify is "active" while its shield is up or lingering.
   * SkillActivationComponent.update() detects the true→false transition
   * and emits 'skill-deactivated', which triggers VFX cleanup in the
   * orchestrator. Without this, InstantSkillEffect.isActive() returns
   * false always, and no deactivation signal ever fires.
   */
  override isActive(): boolean {
    return this.isShieldActive;
  }

  /**
   * Returns true only when: (a) cooldown has fully elapsed, (b) no shield is
   * currently active (prevents re-cast while shield is up or in linger), and
   * (c) the player has been stationary for at least stationaryRequirementMs.
   */
  canActivate(): boolean {
    if (this._cooldownRemaining > 0) return false;
    if (this.deps.playerState.fortifyShieldHp > 0 || this._isLingering) return false;
    return this.deps.stationary.isStationary(this.config.stationaryRequirementMs);
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const shieldHpBonus = this.deps.resolver.getAdditiveEffect('fortify', 'shieldHp');
    const totalShieldHp = this.config.baseShieldHp + shieldHpBonus;
    this.deps.playerState.fortifyShieldHp = totalShieldHp;

    // Stagger absorption: 0 without Bulwark, 1 with Bulwark (getMaxEffect returns undefined → 0).
    const staggerAbsorb = this.deps.resolver.getMaxEffect('fortify', 'staggerAbsorb') ?? 0;
    this.deps.playerState.fortifyStaggerAbsorbsRemaining = staggerAbsorb;

    this._isLingering = false;
    this._lingerRemaining = 0;
    this._activationCount++;

    // CRITICAL: cooldown does NOT start here. It starts when the shield breaks
    // or the linger timer expires. This rewards keeping the shield alive.
    return true;
  }

  /**
   * Called by CombatHitResolver when the player receives damage while Fortify
   * is active. Routes incoming damage through the shield first.
   *
   * Overflow (damage beyond remaining shield HP) is returned to the caller,
   * which then applies it to the player's main HP pool. Shield depletion
   * triggers Rebound (if Rebound is unlocked) and starts the cooldown.
   *
   * Returns zero absorption immediately if no shield is active.
   */
  absorbDamage(incoming: number): DamageAbsorptionResult {
    if (this.deps.playerState.fortifyShieldHp <= 0) {
      const result: DamageAbsorptionResult = {
        absorbed: 0,
        overflow: incoming,
        shieldDepleted: false,
      };
      this._lastAbsorptionResult = result;
      return result;
    }

    const current = this.deps.playerState.fortifyShieldHp;
    const absorbed = Math.min(incoming, current);
    const overflow = incoming - absorbed;

    this.deps.playerState.fortifyShieldHp = current - absorbed;

    let depleted = false;
    if (this.deps.playerState.fortifyShieldHp <= 0) {
      depleted = true;
      this._onShieldDepleted();
    }

    const result: DamageAbsorptionResult = { absorbed, overflow, shieldDepleted: depleted };
    this._lastAbsorptionResult = result;
    return result;
  }

  /**
   * Called by StatusEffectSystem when a stagger is about to be applied to
   * the player. Returns true if the stagger was absorbed (do not apply stagger).
   *
   * The stagger absorption does NOT prevent HP damage from the triggering hit —
   * absorbDamage() handles that separately.
   *
   * Returns false if no absorbs remain or the shield is not active.
   */
  absorbStagger(): boolean {
    if (this.deps.playerState.fortifyShieldHp <= 0) return false;
    if (this.deps.playerState.fortifyStaggerAbsorbsRemaining <= 0) return false;
    this.deps.playerState.fortifyStaggerAbsorbsRemaining -= 1;
    return true;
  }

  /**
   * Called by the game layer when the player begins moving.
   *
   * Starts the 1-second linger timer. The shield remains fully functional
   * during the linger window — it still absorbs damage and blocks staggers.
   * When the timer expires in update(), the shield deactivates and the
   * cooldown starts.
   *
   * No-op if the shield is already inactive or the linger is already running.
   */
  onMovementStart(): void {
    if (this.deps.playerState.fortifyShieldHp <= 0 && !this._isLingering) return;
    if (this._isLingering) return; // Already in linger — don't restart the timer
    this._isLingering = true;
    this._lingerRemaining = this.config.movementLingerMs;
  }

  override update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    if (this._isLingering) {
      this._lingerRemaining = Math.max(0, this._lingerRemaining - delta);
      if (this._lingerRemaining <= 0) {
        // Linger expired — shield deactivates, cooldown starts
        this.deps.playerState.fortifyShieldHp = 0;
        this.deps.playerState.fortifyStaggerAbsorbsRemaining = 0;
        this._isLingering = false;
        this._startCooldown();
      }
    }
  }

  override destroy(): void {
    this._cooldownRemaining = 0;
    this._lingerRemaining = 0;
    this._isLingering = false;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private _onShieldDepleted(): void {
    // Rebound fires before cooldown so CombatHitResolver can process it synchronously
    this._triggerReboundIfUnlocked();

    // Clear any lingering state — shield is gone by damage, not movement
    this._isLingering = false;
    this._lingerRemaining = 0;
    this.deps.playerState.fortifyStaggerAbsorbsRemaining = 0;

    this._startCooldown();
  }

  private _triggerReboundIfUnlocked(): void {
    if (!this.deps.resolver.hasEffect('fortify', 'breakExplosionDamage')) return;

    const explosionDamage = this.deps.resolver.getAdditiveEffect('fortify', 'breakExplosionDamage');
    const explosionRadiusTiles = this.deps.resolver.getAdditiveEffect(
      'fortify',
      'breakExplosionRadiusTiles',
    );
    const knockbackTiles = this.deps.resolver.getAdditiveEffect('fortify', 'breakKnockbackTiles');
    const radiusPx = explosionRadiusTiles * this.config.tileSizePx;

    const playerPos = this.deps.targeting.getPlayerPosition();
    const targets = this.deps.targeting.getEnemiesInRadius(radiusPx);
    this._lastBreakExplosionTargetCount = targets.length;

    for (const targetId of targets) {
      const enemyPos = this.deps.targeting.getEnemyPosition(targetId);
      const dx = enemyPos.x - playerPos.x;
      const dy = enemyPos.y - playerPos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      // Default push direction: downward (away from player) if enemy is at player's exact position
      const nx = dist > 0 ? dx / dist : 0;
      const ny = dist > 0 ? dy / dist : 1;

      this.deps.combat.applyDamage(targetId, explosionDamage, { sourceSkillId: 'fortify' });
      if (knockbackTiles > 0) {
        this.deps.combat.applyKnockback(targetId, { x: nx, y: ny }, knockbackTiles);
      }
    }
  }

  private _startCooldown(): void {
    this._cooldownRemaining = this.config.cooldownMs;
    this._cooldownStartCount++;
  }
}
